import { useNavigation, useRoute } from '@react-navigation/native';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { LevelScreen, LevelSubScreen, NcFileBatchEventType, Perspective } from '@v2/pages/common/enums';
import { navigationLevels } from '@v2/pages/machines/common/constants';
import { MetricType } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { ToolsDetailsScreenName } from '@v2/pages/tools/details/navigation/config';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useDetailsLevels } from './useDetailsLevels';
import { useLevelsSubMenu } from './useLevelsSubMenu';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { useSelector } from 'react-redux';
import { selectHeaderConfig } from '@reducers/details';
import { PERSPECTIVE_PARAMS_MAP, pluralTitleRegExp } from '@v2/pages/common/constants/base.constants';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { ToolsDetailsScreen } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { useGetNavigationParams } from './useGetNavigationParams';

export const useHandleNavigationContextHeader = () => {
  const navigation = useNavigation<any>();
  const route = useRoute();
  const { timePeriod } = useHandleParams();

  const { batchLevelSubScreens, ncFileLevelSubScreens, jobsLevelSubScreens, partLevelSubScreens, productionOrderLevelSubScreens, toolLevelSubScreens } = useLevelsSubMenu();
  const headerConfig = useSelector(selectHeaderConfig);
  const { getNavigationParams } = useGetNavigationParams();

  const perspective = route.params?.perspective as Perspective;

  const { getDetailsLevels } = useDetailsLevels();

  const screenMapper = {
    [Perspective.BATCH]: {
      mainScreen: BatchDetailsScreenName,
      levelScreens: getDetailsLevels(Perspective.BATCH),
    },
    [Perspective.NC_FILE]: {
      mainScreen: NCFileDetailsScreenName,
      levelScreens: getDetailsLevels(Perspective.NC_FILE),
    },
    [Perspective.PART]: {
      mainScreen: PartDetailsScreenName,
      levelScreens: getDetailsLevels(Perspective.PART),
    },
    [Perspective.ORDER]: {
      mainScreen: ProductionOrderDetailsScreenName,
      levelScreens: getDetailsLevels(Perspective.ORDER),
    },
    [Perspective.JOBS]: {
      mainScreen: JobDetailsScreenName,
      levelScreens: getDetailsLevels(Perspective.JOBS),
    },
    [Perspective.TOOLS]: {
      mainScreen: ToolsDetailsScreenName,
      levelScreens: getDetailsLevels(Perspective.TOOLS),
    },
  };

  const screenConfig = screenMapper[perspective];

  // Find both the key and value from levelScreens
  const activeLevelScreenEntry = screenConfig
    ? Object.entries(screenConfig.levelScreens).find(([key, levelName]) => route.name.includes(levelName))
    : null;

  const activeLevelScreenName = activeLevelScreenEntry?.[1] ?? null;
  const activeLevelScreenKey = activeLevelScreenEntry?.[0] ?? null;

  const levelSubScreensList = {
    [LevelScreen.PART]: partLevelSubScreens,
    [LevelScreen.JOB]: jobsLevelSubScreens,
    [LevelScreen.ORDER]: productionOrderLevelSubScreens,
    [LevelScreen.BATCH]: batchLevelSubScreens,
    [LevelScreen.NC_FILE]: ncFileLevelSubScreens,
    [LevelScreen.TOOLS]: toolLevelSubScreens,
  };

  const levelScreenToHeaderConfig = {
    [LevelScreen.PART]: headerConfig?.part?.name,
    [LevelScreen.JOB]: headerConfig?.job,
    [LevelScreen.ORDER]: headerConfig?.productionOrder,
    [LevelScreen.BATCH]: headerConfig?.batch,
    [LevelScreen.NC_FILE]: headerConfig?.ncFile,
    [LevelScreen.TOOLS]: headerConfig?.tools,
  };

  const getActiveLevelName = (level: string = activeLevelScreenName) => {
    if (!level) return [];
    const match = level?.match(/Details(.+)/);
    const activeLevelName = match ? match[1] : '';
    return activeLevelName || '';
  };

  const getActiveLevelSubScreens = (level: string = activeLevelScreenName) => {
    const activeLevelName = getActiveLevelName(level);
    return levelSubScreensList[activeLevelName];
  };

  const activeLevelSubScreenName = Object.values(getActiveLevelSubScreens() ?? {}).find((levelName) => route.name.includes(levelName));

  const activePerformanceSubScreenName = Object.values(navigationLevels.metricTypes).find((mt) => route.name.includes(mt));

  const isPerformanceSubScreen = activeLevelSubScreenName === LevelSubScreen.PERFORMANCE;

  const shouldDisplayModeSelector = route.name.includes('Documents');

  const screensWithEventsMenu = [
    { screen: BatchDetailsScreen.BATCH, condition: true },
    { screen: BatchDetailsScreen.NC_FILE, condition: true },
    { screen: NCFileDetailsScreen.BATCH, condition: !pluralTitleRegExp.test(headerConfig.batch) },
    { screen: NCFileDetailsScreen.NC_FILE, condition: true },
    { screen: PartDetailsScreen.BATCH, condition: !pluralTitleRegExp.test(headerConfig.batch) },
    { screen: PartDetailsScreen.NC_FILE, condition: !pluralTitleRegExp.test(headerConfig.ncFile) },
    { screen: JobDetailsScreen.BATCH, condition: !pluralTitleRegExp.test(headerConfig.batch) },
    { screen: JobDetailsScreen.NC_FILE, condition: !pluralTitleRegExp.test(headerConfig.ncFile) },
    { screen: ToolsDetailsScreen.BATCH, condition: !pluralTitleRegExp.test(headerConfig.batch) },
    { screen: ToolsDetailsScreen.NC_FILE, condition: !pluralTitleRegExp.test(headerConfig.ncFile) },
    { screen: ProductionOrderDetailsScreen.BATCH, condition: !pluralTitleRegExp.test(headerConfig.batch) },
    { screen: ProductionOrderDetailsScreen.NC_FILE, condition: !pluralTitleRegExp.test(headerConfig.ncFile) },
  ];

  const shouldDisplayNcFileBatchEventsSubLevelMenu =
    activeLevelSubScreenName === LevelSubScreen.TIMES &&
    activeLevelScreenName &&
    screensWithEventsMenu.find((s) => activeLevelScreenName.includes(s.screen) && !!s.condition);

  const activeNcFileBatchEventsSubScreenName = shouldDisplayNcFileBatchEventsSubLevelMenu
    ? Object.values(NcFileBatchEventType).find((m) => route.name?.includes(m))
    : null;

  const handleNavigateDetailsLevel = ({
    levelScreen,
    mainScreen = screenConfig.mainScreen,
    params,
  }: {
    levelScreen: string;
    mainScreen?: string;
    params?: Record<string, unknown>;
  }) => {
    const activeLevelName = getActiveLevelName(levelScreen);
    const activeLevelHeaderText = levelScreenToHeaderConfig[activeLevelName];
    const isActiveLevelHeaderTextPlural = activeLevelHeaderText && pluralTitleRegExp.test(activeLevelHeaderText);

    const possibleLevelSubScreens = getActiveLevelSubScreens(levelScreen);
    const withoutLevelSubScreens = !possibleLevelSubScreens.length;

    const levelSubScreen = withoutLevelSubScreens
      ? LevelSubScreen.GENERAL
      : activeLevelSubScreenName && possibleLevelSubScreens.includes(activeLevelSubScreenName) && !isActiveLevelHeaderTextPlural
      ? activeLevelSubScreenName
      : LevelSubScreen.GENERAL;

    if (levelSubScreen === LevelSubScreen.PERFORMANCE || (levelSubScreen === LevelSubScreen.TIMES && activeNcFileBatchEventsSubScreenName)) {
      navigation.navigate(mainScreen, {
        screen: levelScreen,
        params: {
          screen: levelScreen + levelSubScreen,
          params: {
            screen:
              levelScreen +
              levelSubScreen +
              (levelSubScreen === LevelSubScreen.TIMES ? activeNcFileBatchEventsSubScreenName : activePerformanceSubScreenName),
            params: {
              ...route.params,
              ...params,
            },
          },
        },
      });
    } else {
      navigation.navigate(mainScreen, {
        screen: levelScreen,
        params: {
          ...(!levelSubScreen
            ? { ...route.params, perspective: route.params?.perspective }
            : {
                screen: levelScreen + levelSubScreen,
                params: {
                  ...route.params,
                  perspective: route.params?.perspective,
                  ...(isPerformanceSubScreen ? { timePeriod } : {}),
                  ...params,
                },
              }),
        },
      });
    }
  };

  const handleNavigateDetailsLevelSubScreen = (levelSubScreen: string) => {
    if (!activeLevelScreenName) return;

    trackEvent(TRACKING_EVENTS.INFO_BAR, {
      action: 'selectionChanged',
      fromPerspective: activeLevelSubScreenName || '',
      toPerspective: levelSubScreen || '',
    });

    if (levelSubScreen === LevelSubScreen.PERFORMANCE) {
      navigation.navigate(screenConfig.mainScreen, {
        screen: activeLevelScreenName,
        params: {
          screen: activeLevelScreenName + levelSubScreen,
          params: {
            screen: activeLevelScreenName + levelSubScreen + navigationLevels.metricTypes.active,
            params: {
              ...route.params,
              timePeriod,
            },
          },
        },
      });
    } else {
      navigation.navigate(screenConfig.mainScreen, {
        screen: activeLevelScreenName,
        params: {
          screen: activeLevelScreenName + levelSubScreen,
          params: {
            ...route.params,
            perspective: route.params?.perspective,
            ...(levelSubScreen === LevelSubScreen.PERFORMANCE ? { timePeriod } : {}),
          },
        },
      });
    }
  };

  const handleNavigateNestedPerformanceSubScreen = (performanceSubScreen: MetricType) => {
    const levelSubScreen = activeLevelSubScreenName ?? LevelSubScreen.GENERAL;

    navigation.navigate(screenConfig.mainScreen, {
      screen: activeLevelScreenName,
      params: {
        screen: activeLevelScreenName + levelSubScreen,
        params: {
          screen: activeLevelScreenName + levelSubScreen + performanceSubScreen,
          params: { ...route.params, timePeriod },
        },
      },
    });
  };

  const handleNavigateNcFileBatchEvents = (eventSubScreen: NcFileBatchEventType) => {
    if (!activeNcFileBatchEventsSubScreenName) return;
    trackEvent(TRACKING_EVENTS.TIMES, {
      action: 'subScreenChanged',
      perspective: 'ncFile' as const,
      subScreen: eventSubScreen.toLowerCase() as 'cycle' | 'active' | 'change' | 'setup' | 'trace',
      ncFileId: String(route.params?.ncFileId || ''),
    });
    const newScreenName = route.name.slice(0, route.name.length - activeNcFileBatchEventsSubScreenName.length);
    navigation?.navigate(newScreenName + eventSubScreen, { ...route.params });
  };

  const checkWithPerspectiveBadge = ({ preselected, selected, plural }: { selected: boolean; preselected: boolean; plural: boolean }) =>
    selected && !preselected && !plural;

  const onPerspectiveBadgePress = ({ perspectiveToGo }: { perspectiveToGo: Perspective }) => {
    if (!perspectiveToGo) return;

    const newScreenConfig = screenMapper[perspectiveToGo];

    const newPerspectiveMainScreen = newScreenConfig.mainScreen;

    const newActiveLevelScreenName = newScreenConfig.levelScreens?.[activeLevelScreenKey];

    const allPossibleNavigationParams = getNavigationParams(perspectiveToGo);
    const requiredParamKeys = PERSPECTIVE_PARAMS_MAP[perspectiveToGo];
    const navigationParams: Record<string, any> = requiredParamKeys.reduce((params, key) => {
      params[key] = allPossibleNavigationParams[key];
      return params;
    }, {} as Record<string, any>);

    handleNavigateDetailsLevel({ levelScreen: newActiveLevelScreenName, mainScreen: newPerspectiveMainScreen, params: navigationParams });
    return;
  };

  return {
    handleNavigateDetailsLevel,
    handleNavigateDetailsLevelSubScreen,
    activeLevelScreenName,
    activeLevelSubScreenName,
    activeNcFileBatchEventsSubScreenName,
    handleNavigateNestedPerformanceSubScreen,
    handleNavigateNcFileBatchEvents,
    screenConfig,
    activePerformanceSubScreenName,
    shouldDisplayModeSelector,
    shouldDisplayNcFileBatchEventsSubLevelMenu,
    checkWithPerspectiveBadge,
    onPerspectiveBadgePress,
  };
};
